import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { courseAPI } from '../api/courseApi';
import Carousel from '../components/Carousel';
import TrendingCourseCard from '../components/TrendingCourseCard';
import Loader from '../components/Loader';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';

const Home = () => {
  const { userInfo } = useAuth();
  const [loading, setLoading] = useState(true);
  const [banners, setBanners] = useState([]);
  const [courses, setCourses] = useState([]);

  const updatePurchaseStatus = useCallback(async () => {
    try {
      const userId = userInfo?.user_detail?.id;
      if (!userId) return;
      
      // Fetch purchased courses using getMyPurchased
      const purchasedResponse = await courseAPI.getMyPurchased(userId);
      console.log('My Purchased courses for Home:', purchasedResponse);
      
      if (purchasedResponse && purchasedResponse.data) {
        const purchasedCourses = purchasedResponse.data;
        
        if (Array.isArray(purchasedCourses) && purchasedCourses.length > 0) {
          // Merge purchase status with existing courses
          setCourses(prevCourses => {
            return prevCourses.map(course => {
              const purchased = purchasedCourses.find(p => String(p.id) === String(course.id));
              if (purchased) {
                return { 
                  ...course, 
                  purchased: 'Credit',
                  group_url: purchased.group_url || course.group_url,
                  type: purchased.type || course.type,
                  viewType: purchased.viewType || course.viewType
                };
              }
              return course;
            });
          });
        }
      }
    } catch (error) {
      console.error('Error updating purchase status:', error);
      // Don't show error to user, just keep existing courses
    }
  }, [userInfo]);

  const fetchHomeData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch carousel data (banners) - matching mobile app
      const carouselResponse = await courseAPI.getCarousel();
      if (carouselResponse && carouselResponse.data) {
        setBanners(carouselResponse.data);
      }
      
      // Always fetch trending courses
      const trendingResponse = await courseAPI.getTrending();
      console.log('Trending courses response:', trendingResponse);
      
      if (trendingResponse && trendingResponse.data) {
        let coursesData = trendingResponse.data;
        setCourses(Array.isArray(coursesData) ? coursesData : []);
        
        // If user is logged in, update with purchase status
        const userId = userInfo?.user_detail?.id;
        if (userId) {
          await updatePurchaseStatus();
        }
      }
    } catch (error) {
      console.error('Error fetching home data:', error);
      toast.error('Failed to load home data. Please check your internet connection.');
    } finally {
      setLoading(false);
    }
  }, [userInfo, updatePurchaseStatus]);

  useEffect(() => {
    fetchHomeData();
  }, [fetchHomeData]); // Fetch on mount and when fetchHomeData changes

  useEffect(() => {
    // Re-fetch when user logs in/out to update purchase status
    if (courses.length > 0 && userInfo) {
      updatePurchaseStatus();
    }
  }, [userInfo, courses.length, updatePurchaseStatus]);

  if (loading) return <Loader />;

  return (
    <div className="min-h-screen">
      {/* Hero Carousel */}
      {banners.length > 0 && <Carousel items={banners} />}

      {/* Features Section */}
      <section className="py-8 sm:py-12 lg:py-16 bg-gray-50">
        <div className="container">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {/* Feature 1 */}
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition text-center">
              <div className="text-5xl mb-4">📚</div>
              <h4 className="text-xl font-bold mb-2 text-gray-800">Apply Online</h4>
              <p className="text-gray-600 text-sm">
                We Provide online education. Jharkhand No.1 Youtube Channel
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition text-center">
              <div className="text-5xl mb-4">🎯</div>
              <h4 className="text-xl font-bold mb-2 text-gray-800">Superfast Support</h4>
              <p className="text-gray-600 text-sm">
                We Provide Superfast Support to all our students
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition text-center">
              <div className="text-5xl mb-4">👨‍🏫</div>
              <h4 className="text-xl font-bold mb-2 text-gray-800">10+ Years Experience</h4>
              <p className="text-gray-600 text-sm">
                We have more than 10 years of teaching experience
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition text-center">
              <div className="text-5xl mb-4">💳</div>
              <h4 className="text-xl font-bold mb-2 text-gray-800">Online Payment</h4>
              <p className="text-gray-600 text-sm">
                We accept secure online payments and provide online tests
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trending Courses Section - Matches Mobile App */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="container">
          {/* Section Header */}
          <div className="text-center mb-8 sm:mb-12">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 mb-3">
              Career Foundation
            </h1>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-blue-600 mb-4">
              Best Coaching Center for Jharkhand Govt. Exams Preparation
            </h2>
            <h3 className="text-lg sm:text-xl font-bold text-gray-700 bg-purple-100 inline-block px-6 py-2 rounded-full border-2 border-purple-300">
              Trending Courses
            </h3>
          </div>

          {/* Trending Courses Grid - 3 columns matching mobile app */}
          {courses.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
                {courses.map((course) => (
                  <TrendingCourseCard key={course.id} course={course} />
                ))}
              </div>

              <div className="text-center mt-10 sm:mt-12">
                <Link 
                  to="/courses" 
                  className="btn btn-primary btn-large inline-block"
                >
                  View All Courses →
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p className="text-xl">No courses available at the moment.</p>
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container">
          {/* Section Header */}
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">Why Choose Us</h2>
            <h3 className="text-xl sm:text-2xl mb-3">What We Provide</h3>
            <p className="text-base sm:text-lg opacity-90 max-w-3xl mx-auto">
              We provide the best online education for general competition, computer, and coding classes.
            </p>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl text-center hover:bg-white/20 transition">
              <div className="text-5xl mb-4">✅</div>
              <h4 className="text-xl font-bold mb-2">Expert Faculty</h4>
              <p className="text-sm opacity-90">
                Learn from experienced teachers with proven track records
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl text-center hover:bg-white/20 transition">
              <div className="text-5xl mb-4">📱</div>
              <h4 className="text-xl font-bold mb-2">Mobile Learning</h4>
              <p className="text-sm opacity-90">
                Access courses anytime, anywhere on your mobile device
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl text-center hover:bg-white/20 transition">
              <div className="text-5xl mb-4">📊</div>
              <h4 className="text-xl font-bold mb-2">Test Series</h4>
              <p className="text-sm opacity-90">
                Practice with comprehensive test series and mock exams
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl text-center hover:bg-white/20 transition">
              <div className="text-5xl mb-4">🏆</div>
              <h4 className="text-xl font-bold mb-2">Proven Results</h4>
              <p className="text-sm opacity-90">
                Thousands of successful students across Jharkhand
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
